import { forwardRef, useImperativeHandle, useRef, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { ModalCenter, ModalCenterRef } from '@shared/components/ui/ModalCenter';
import { Button } from '@shared/components/ui/button';
import { LoadingState, ErrorState } from '../feedback';
import { faNoteSticky, faPlus, faEllipsisVertical, faPen, faTrash } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useNotes } from '../../hooks/useNotes';
import { NoteModel } from '../../models/Note';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@shared/components/ui/dropdown-menu';
import { useToast } from '@shared/hooks/use-toast';
import { ConfirmModal } from '@shared/components/ui/ConfirmModal';
import type { ConfirmModalRef } from '@shared/components/ui/ConfirmModal';
import { NewNoteModal, NewNoteModalRef } from './NewNoteModal';
import { useCanManageCRM } from '@modules/CRM/shared/hooks/useCRMSettings';

export interface NotesModalRef {
  open: (orderId: number) => void;
}

interface NotesModalProps {
  onSuccess?: () => void;
}

export const NotesModal = forwardRef<NotesModalRef, NotesModalProps>(({ onSuccess }, ref) => {
  const { t } = useTranslation('crmModule');
  const { toast } = useToast();
  const modalRef = useRef<ModalCenterRef>(null);
  const confirmModalRef = useRef<ConfirmModalRef>(null);
  const newNoteModalRef = useRef<NewNoteModalRef>(null);
  const [orderId, setOrderId] = useState<number | undefined>(undefined);

  const { notes, isLoading, error, refetch, deleteNote } = useNotes(orderId);
  const { canManage } = useCanManageCRM();

  useImperativeHandle(ref, () => ({
    open: (orderIdParam: number) => {
      setOrderId(orderIdParam);
      modalRef.current?.openModal();
    },
  }));

  useEffect(() => {
    if (orderId) {
      refetch();
    }
  }, [orderId, refetch]);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const time = date.toLocaleTimeString('es-ES', {
      hour: '2-digit',
      minute: '2-digit',
    });
    const dateFormatted = date.toLocaleDateString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
    return `${time} · ${dateFormatted}`;
  };

  const handleStartEdit = (note: NoteModel) => {
    if (orderId !== undefined) {
      newNoteModalRef.current?.openForEdit(orderId, note.id, note.content);
    }
  };

  const handleDeleteNote = (noteId: number) => {
    confirmModalRef.current?.open(
      async () => {
        try {
          await deleteNote(noteId);
          toast({
            title: t('notes.deleteSuccess.title', 'Nota eliminada'),
            description: t('notes.deleteSuccess.description', 'La nota se ha eliminado correctamente.'),
            variant: 'default',
          });
          onSuccess?.();
        } catch (error: any) {
          toast({
            title: t('notes.deleteError.title', 'Error al eliminar nota'),
            description:
              error?.response?.data?.message ||
              t('notes.deleteError.description', 'Error inesperado al eliminar la nota.'),
            variant: 'destructive',
          });
        }
      },
      undefined,
      t('notes.deleteConfirm.title', 'Eliminar nota'),
      t('notes.deleteConfirm.message', '¿Estás seguro de que deseas eliminar esta nota?'),
    );
  };

  const handleOpenNewNoteModal = () => {
    if (orderId !== undefined) {
      newNoteModalRef.current?.open(orderId);
    }
  };

  return (
    <>
      <ModalCenter
        ref={modalRef}
        title={t('notes.modal.title', 'Notas')}
        type="default"
        width="lg"
        className="h-[100dvh] sm:h-auto w-full sm:w-[600px] max-h-[90vh]"
      >
        <div className="flex flex-col h-full max-h-[calc(100vh-200px)]">
          {/* Lista de notas */}
          <div className="flex-1 overflow-y-auto space-y-4 pr-2">
            {isLoading ? (
              <LoadingState className="min-h-[200px]" />
            ) : error ? (
              <ErrorState error={error as Error} onRetry={refetch} className="min-h-[200px]" />
            ) : notes.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 text-center">
                <FontAwesomeIcon icon={faNoteSticky} className="w-12 h-12 text-complementary-text mb-4" />
                <p className="text-text font-medium mb-1">{t('notes.empty.title', 'No hay notas')}</p>
                <p className="text-sm text-complementary-text">
                  {t('notes.empty.description', 'Aún no se han agregado notas para este pedido.')}
                </p>
              </div>
            ) : (
              notes.map((note) => (
                <div key={note.id} className="border border-card-border rounded-lg p-4 space-y-2">
                  {/* Mensaje y menú de acciones en la misma línea - Message and actions menu on same row */}
                  <div className="flex items-start justify-between gap-2">
                    <p className="text-text text-sm whitespace-pre-wrap flex-1 min-w-0">{note.content}</p>
                    {canManage ? (
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <button
                            type="button"
                            className="flex items-center justify-center w-6 h-6 rounded-md hover:bg-hover-press transition-colors shrink-0"
                            data-testid={`note-actions-${note.id}`}
                          >
                            <FontAwesomeIcon icon={faEllipsisVertical} className="w-3.5 h-3.5 text-icons" />
                          </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => handleStartEdit(note)} data-testid={`edit-note-${note.id}`}>
                            <FontAwesomeIcon icon={faPen} className="mr-2 w-3.5 h-3.5" />
                            {t('notes.edit', 'Editar')}
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() => handleDeleteNote(note.id)}
                            data-testid={`delete-note-${note.id}`}
                          >
                            <FontAwesomeIcon icon={faTrash} className="mr-2 w-3.5 h-3.5" />
                            {t('notes.delete', 'Eliminar')}
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    ) : null}
                  </div>
                  {/* Autor y fecha - Author and date */}
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-complementary-text">
                      {note.creator?.name || t('notes.unknownAuthor', 'Usuario desconocido')}
                    </span>
                    <span className="text-xs text-complementary-text">{formatDate(note.created_at)}</span>
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Botón para abrir modal de nueva nota - Button to open new note modal */}
          <div className="mt-4 flex justify-end">
            <Button
              variant="outline"
              size="sm"
              onClick={handleOpenNewNoteModal}
              disabled={!canManage}
              className="flex items-center gap-2"
              data-testid="new-note-button"
            >
              <FontAwesomeIcon icon={faPlus} className="w-3.5 h-3.5" />
              {t('notes.new', 'Nueva')}
            </Button>
          </div>
        </div>
      </ModalCenter>
      <NewNoteModal
        ref={newNoteModalRef}
        onSuccess={() => {
          refetch();
          onSuccess?.();
        }}
      />
      <ConfirmModal ref={confirmModalRef} />
    </>
  );
});

NotesModal.displayName = 'NotesModal';
